import { useEffect, useRef } from 'react';
import DynamicFormOutlined from '@mui/icons-material/DynamicFormOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import type { FormDetail } from '../api/catalog';
import { COPY } from '../copy';
import { formatDateTime } from '../format/format';
import type { DetailTab } from './catalogParams';
import { FormDownloads } from './FormDownloads';
import { kindLabel, renditionLabel } from './labels';
import { PageViewer } from './PageViewer';
import { PreviewUnavailable } from './PreviewUnavailable';
import type { useFormDetail } from './useFormDetail';

interface FormDetailPaneProps {
  detail: ReturnType<typeof useFormDetail>;
  tab: DetailTab;
  headingLevel: 'h1' | 'h2';
  focusOnShow: boolean;
  onTab: (tab: DetailTab) => void;
  page: number | null;
  onPage: (page: number) => void;
  onNotFoundBack: () => void;
}

export function FormDetailPane({
  detail,
  tab,
  headingLevel,
  focusOnShow,
  onTab,
  page,
  onPage,
  onNotFoundBack,
}: FormDetailPaneProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const form = detail.form;
  const formId = form?.id;

  useEffect(() => {
    if (!focusOnShow || formId === undefined) return;
    headingRef.current?.focus();
  }, [focusOnShow, formId]);

  if (detail.status === 'loading') {
    return <LinearProgress aria-label={COPY.loadingForm} />;
  }

  if (detail.status === 'error' && detail.error) {
    if (detail.error.status === 404) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {COPY.notAvailableTitle}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {COPY.notAvailableBody}
          </Typography>
          <Button variant="outlined" onClick={onNotFoundBack}>
            {COPY.backToForms}
          </Button>
        </Box>
      );
    }
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={detail.retry} color="inherit">
            {COPY.tryAgain}
          </Button>
        }
        sx={{ m: 2 }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {COPY.formErrorTitle}
        </Typography>
        <Typography variant="body2">
          {COPY.reference(detail.error.requestId ?? detail.error.code)}
        </Typography>
      </Alert>
    );
  }

  if (detail.status !== 'ready' || !form) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {COPY.selectFormPrompt}
        </Typography>
      </Box>
    );
  }

  const formats =
    form.kind === 'ADAPTIVE' ? COPY.kindAdaptive : form.renditions.map(renditionLabel).join(', ');

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', mb: 2 }}>
        {form.kind === 'XDP' ? (
          <PictureAsPdfOutlined aria-hidden fontSize="large" />
        ) : (
          <DynamicFormOutlined aria-hidden fontSize="large" />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant={headingLevel === 'h1' ? 'h1' : 'h2'}
            component={headingLevel}
            tabIndex={-1}
            ref={headingRef}
          >
            {form.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {form.code} · {kindLabel(form.kind)} · {COPY.versionN(form.versionNo)}
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => onTab('form')}>
          {COPY.openForm}
        </Button>
      </Stack>
      <Tabs
        value={tab}
        onChange={(_event, value: string) => {
          if (value === 'details' || value === 'form') onTab(value);
        }}
        aria-label={COPY.formViewsLabel}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab
          value="details"
          label={COPY.tabDetails}
          id="form-tab-details"
          aria-controls="form-tabpanel-details"
        />
        <Tab
          value="form"
          label={COPY.tabForm}
          id="form-tab-form"
          aria-controls="form-tabpanel-form"
        />
      </Tabs>
      <Box role="tabpanel" id={`form-tabpanel-${tab}`} aria-labelledby={`form-tab-${tab}`}>
        {tab === 'details' ? (
          <Details form={form} formats={formats} />
        ) : (
          <FormTab form={form} page={page} onPage={onPage} />
        )}
      </Box>
    </Box>
  );
}

function Details({ form, formats }: { form: FormDetail; formats: string }) {
  const rows: Array<{ label: string; value: string }> = [
    { label: COPY.labelDescription, value: form.description },
    {
      label: COPY.labelCategories,
      value: form.categories.map((category) => category.name).join(', '),
    },
    { label: COPY.labelOwner, value: form.owner },
    { label: COPY.labelDepartment, value: form.owningDepartment },
    { label: COPY.labelSecondaryContact, value: form.secondaryContact },
    { label: COPY.labelCompany, value: form.company },
    { label: COPY.labelOrdering, value: form.orderInstructions },
    { label: COPY.labelFormats, value: formats },
    { label: COPY.labelVersion, value: String(form.versionNo) },
    { label: COPY.labelPublished, value: formatDateTime(form.publishedAt) },
  ];

  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'max-content 1fr' },
        columnGap: 3,
        rowGap: 1,
        m: 0,
      }}
    >
      {rows.map((row) => (
        <DetailRow key={row.label} label={row.label} value={row.value} />
      ))}
    </Box>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  const empty = value === '';
  return (
    <>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        color={empty ? 'text.secondary' : 'text.primary'}
        sx={{ m: 0, mb: { xs: 1, lg: 0 }, whiteSpace: 'pre-line' }}
      >
        {empty ? COPY.notProvided : value}
      </Typography>
    </>
  );
}

function FormTab({
  form,
  page,
  onPage,
}: {
  form: FormDetail;
  page: number | null;
  onPage: (page: number) => void;
}) {
  if (form.kind === 'ADAPTIVE') {
    return (
      <>
        <Chip label={COPY.comingSoon} size="small" variant="outlined" sx={{ mb: 2 }} />
        <Typography variant="body1">{COPY.formPlaceholderAdaptive}</Typography>
      </>
    );
  }
  return (
    <Stack spacing={2}>
      <FormDownloads form={form} />
      {form.pageCount === null ? (
        <PreviewUnavailable />
      ) : (
        <PageViewer form={form} page={page} onPage={onPage} />
      )}
    </Stack>
  );
}
